import {
  Form,
  data,
  redirect,
  useActionData,
  useSearchParams,
} from "react-router";
import { getFormProps, getInputProps, useForm } from "@conform-to/react";
import {
  EmailSchema,
  PasswordSchema,
  UsernameSchema,
} from "~/lib/utils/user-validation";
import { z } from "zod";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "react-router";
import { getUserId, login, requireAnonymous } from "~/lib/utils/auth.server";
import { tenantRedirect } from "~/components/tenant-link";
import { HoneypotInputs } from "remix-utils/honeypot/react";
import { CheckboxField, ErrorList, Field } from "~/components/forms";
import { StatusButton } from "~/components/ui/status-button";
import { useIsPending } from "~/lib/utils/misc";
import { checkHoneypot } from "~/lib/utils/honeypot.server";
import { getZodConstraint, parseWithZod } from "@conform-to/zod";
import { handleNewSession } from "./login.server";
import { ProviderConnectionForm, providerNames } from "~/lib/utils/connections";
import { Icon } from "~/components/ui/icon";
import { GeneralErrorBoundary } from "~/components/error-boundary";
import { db } from "~/lib/db";
import { TenantLink } from "~/components/tenant-link";

const LoginFormSchema = z.object({
  emailOrUsername: z.union([UsernameSchema, EmailSchema]),
  password: PasswordSchema,
  redirectTo: z.string().optional(),
  remember: z.boolean().optional(),
});

export async function loader({ request }: LoaderFunctionArgs) {
  const userId = await getUserId(request);

  // If already logged in, redirect to home
  if (userId) {
    // Optional: Check if user has a team and redirect to appropriate page
    const teamMember = await db.query.teamMembers.findFirst({
      where: (member, { eq }) => eq(member.userId, userId),
      with: { team: true },
    });

    if (teamMember) {
      const host = request.headers.get("host") || "";
      const isDev = process.env.NODE_ENV === "development" || host.includes("vercel.app");
      return tenantRedirect({ to: "/dashboard", tenant: teamMember.team.slug, isDev });
    }
    return redirect("/team-onboarding");
  }

  return data({});
}

export async function action({ request }: ActionFunctionArgs) {
  await requireAnonymous(request);

  const formData = await request.formData();
  checkHoneypot(formData);

  const submission = await parseWithZod(formData, {
    schema: (intent) =>
      LoginFormSchema.transform(async (data, ctx) => {
        if (intent !== null) return { ...data, session: null };

        const session = await login(data);
        if (!session) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Invalid email, username or password",
          });
          return z.NEVER;
        }

        return { ...data, session };
      }),
    async: true,
  });

  if (submission.status !== "success" || !submission.value.session) {
    return data(
      { result: submission.reply({ hideFields: ["password"] }) },
      { status: submission.status === "error" ? 400 : 200 }
    );
  }

  const { session, remember, redirectTo } = submission.value;

  return handleNewSession({
    request,
    session,
    remember: remember ?? false,
    redirectTo,
  });
}

export default function LoginRoute() {
  const actionData = useActionData<typeof action>();
  const isPending = useIsPending();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirectTo");

  const [form, fields] = useForm({
    id: "login-form",
    constraint: getZodConstraint(LoginFormSchema),
    defaultValue: { redirectTo },
    lastResult: actionData?.result,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: LoginFormSchema });
    },
    shouldRevalidate: "onBlur",
  });

  return (
    <div className="flex flex-col items-center justify-center w-full gap-10 h-dvh">
      <div className="flex flex-col gap-3 text-center">
        <h1 className="font-black text-h1">Iniciar sesión</h1>
        <p className="text-body-md text-muted-foreground">
          ¡Bienvenido de vuelta!
        </p>
      </div>
      <div className="w-full md:max-w-[320px] p-6">
        <Form
          method="POST"
          className="flex flex-col gap-10"
          {...getFormProps(form)}
        >
          <div className="flex flex-col gap-4">
            <HoneypotInputs />
            <Field
              labelProps={{ children: "Tu email o nombre de usuario" }}
              inputProps={{
                ...getInputProps(fields.emailOrUsername, { type: "text" }),
                autoFocus: true,
                className: "lowercase",
                autoComplete: "username",
              }}
              errors={fields.emailOrUsername.errors}
            />
            <Field
              labelProps={{ children: "Tu contraseña" }}
              inputProps={{
                ...getInputProps(fields.password, {
                  type: "password",
                }),
                autoComplete: "current-password",
              }}
              errors={fields.password.errors}
            />
            <div className="flex items-start justify-between">
              <CheckboxField
                labelProps={{
                  htmlFor: fields.remember.id,
                  children: "Recuérdame",
                }}
                buttonProps={getInputProps(fields.remember, {
                  type: "checkbox",
                })}
                errors={fields.remember.errors}
              />
              <TenantLink
                to="/forgot-password"
                className="font-semibold text-body-sm"
              >
                Olvidaste tu contraseña?
              </TenantLink>
            </div>

            <input {...getInputProps(fields.redirectTo, { type: "hidden" })} />
            <ErrorList errors={form.errors} id={form.errorId} />
          </div>

          <StatusButton
            type="submit"
            status={isPending ? "pending" : form.status ?? "idle"}
            disabled={isPending}
            className="flex items-center group"
          >
            Iniciar
            <Icon
              name="arrow-right"
              className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-1 transition-all -mr-2.5"
            />
          </StatusButton>
        </Form>

        <p className="my-3 text-sm text-center text-muted-foreground">o</p>

        <ul className="flex flex-col gap-5">
          {providerNames.map((providerName) => (
            <li key={providerName}>
              <ProviderConnectionForm
                type="login"
                providerName={providerName}
                redirectTo={redirectTo}
              />
            </li>
          ))}
        </ul>

        <div className="flex items-center justify-center gap-2 mt-3 text-body-sm">
          Nuevo?
          <TenantLink
            to={
              redirectTo
                ? `/signup?${encodeURIComponent(redirectTo)}`
                : "/signup"
            }
            className="font-semibold underline"
          >
            Crea tu cuenta aquí
          </TenantLink>
        </div>
      </div>
    </div>
  );
}

export const meta: MetaFunction = () => {
  return [{ title: "Iniciar sesión | Fuel Portfolio" }];
};

export function ErrorBoundary() {
  return <GeneralErrorBoundary />;
}
